import { loadDspModule } from './dspModule';
import { dspAssetUrl } from './dspAssetUrl';

const WORKLET_URL = dspAssetUrl('/audio/samplePlaybackProcessor.js');
const workletLoads = new WeakMap();

export async function createSamplePlaybackNode(context) {
  if (!context?.audioWorklet || typeof globalThis.AudioWorkletNode !== 'function') return null;
  let workletLoad = workletLoads.get(context);
  if (!workletLoad) {
    workletLoad = context.audioWorklet.addModule(WORKLET_URL);
    workletLoads.set(context, workletLoad);
  }
  try {
    const [, dspModule] = await Promise.all([workletLoad, loadDspModule()]);
    if (!dspModule) return null;
    return new globalThis.AudioWorkletNode(context, 'cookup-sample-playback', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      processorOptions: { dspModule }
    });
  } catch (_) {
    workletLoads.delete(context);
    return null;
  }
}

export function loadSampleIntoNode(node, slot, audioBuffer, requestId = null) {
  if (!node?.port || !audioBuffer || audioBuffer.length <= 0 || audioBuffer.numberOfChannels <= 0) {
    return false;
  }
  const left = new Float32Array(audioBuffer.getChannelData(0));
  const right = audioBuffer.numberOfChannels > 1
    ? new Float32Array(audioBuffer.getChannelData(1))
    : null;
  const message = { type: 'loadSample', slot, left, right, requestId };
  const transfers = right ? [left.buffer, right.buffer] : [left.buffer];
  node.port.postMessage(message, transfers);
  return true;
}

export function scheduleSampleOnNode(node, slot, whenSeconds, contextSampleRate, options = {}) {
  if (!node?.port) return false;
  const sampleRate = Number(contextSampleRate);
  const when = Number(whenSeconds);
  if (!Number.isFinite(sampleRate) || sampleRate <= 0 || !Number.isFinite(when)) return false;
  const framesFrom = (frameValue, secondsValue, fallback = 0) => {
    const explicit = Number(frameValue);
    if (Number.isFinite(explicit)) return Math.max(0, Math.round(explicit));
    const seconds = Number(secondsValue);
    return Number.isFinite(seconds) ? Math.max(0, Math.round(seconds * sampleRate)) : fallback;
  };
  node.port.postMessage({
    type: 'schedule',
    slot,
    whenFrame: Math.max(0, Math.round(when * sampleRate)),
    rate: options.rate ?? 1,
    gain: options.gain ?? 1,
    pan: options.pan ?? 0,
    startPosition: framesFrom(options.startPosition, options.startSeconds),
    endPosition: framesFrom(options.endPosition, options.endSeconds),
    attackFrames: framesFrom(options.attackFrames, Number(options.attackMs) / 1000, 16),
    releaseFrames: framesFrom(options.releaseFrames, Number(options.releaseMs) / 1000, 48),
    durationFrames: framesFrom(options.durationFrames, options.durationSeconds),
    chokeGroup: Math.max(0, Math.round(Number(options.chokeGroup) || 0)),
    chokeFrames: framesFrom(options.chokeFrames, Number(options.chokeMs) / 1000, 384),
    owner: Math.max(0, Math.round(Number(options.owner) || 0))
  });
  return true;
}

export const clearSampleFromNode = (node, slot) => {
  node?.port?.postMessage({ type: 'clearSample', slot });
};

export const stopSamplePlaybackNode = (node) => {
  node?.port?.postMessage({ type: 'allNotesOff' });
};

export const cancelSamplePlaybackOwner = (node, owner, fadeFrames = 384) => {
  node?.port?.postMessage({ type: 'cancelOwner', owner, fadeFrames });
};

export const destroySamplePlaybackNode = (node) => {
  node?.port?.postMessage({ type: 'destroy' });
  node?.disconnect?.();
};

export class SamplePlaybackPool {
  constructor(node, capacity = 64) {
    this.node = node;
    this.backend = 'initializing';
    this.backendVersion = null;
    this.backendSampleRate = null;
    this.capacity = Math.max(1, Math.min(64, Math.floor(Number(capacity) || 64)));
    this.slots = new Array(this.capacity).fill(null);
    this.bufferSlots = new WeakMap();
    this.bufferLoads = new WeakMap();
    this.ready = new Set();
    this.pending = new Map();
    this.nextRequestId = 1;
    this.handleMessage = (event) => {
      const message = event?.data || {};
      if (message.type === 'backend') {
        this.backend = message.backend || 'unavailable';
        this.backendVersion = Number.isFinite(message.version) ? message.version : null;
        this.backendSampleRate = Number.isFinite(message.sampleRate) ? message.sampleRate : null;
        return;
      }
      if (message.type !== 'sampleLoaded') return;
      const pending = this.pending.get(message.requestId);
      if (!pending || pending.slot !== message.slot) return;
      this.pending.delete(message.requestId);
      this.bufferLoads.delete(pending.buffer);
      if (message.loaded) {
        this.ready.add(pending.slot);
        pending.resolve(pending.slot);
      } else {
        this.bufferSlots.delete(pending.buffer);
        this.slots[pending.slot] = null;
        pending.resolve(null);
      }
    };
    this.node?.port?.addEventListener?.('message', this.handleMessage);
    this.node?.port?.start?.();
  }

  getDiagnostics() {
    return {
      backend: this.backend,
      version: this.backendVersion,
      sampleRate: this.backendSampleRate,
      readySamples: this.ready.size,
      capacity: this.capacity,
      pendingSamples: this.pending.size
    };
  }

  slotFor(audioBuffer) {
    const slot = audioBuffer ? this.bufferSlots.get(audioBuffer) : undefined;
    return Number.isInteger(slot) && this.ready.has(slot) ? slot : null;
  }

  load(audioBuffer) {
    if (!audioBuffer) return Promise.resolve(null);
    const readySlot = this.slotFor(audioBuffer);
    if (readySlot !== null) return Promise.resolve(readySlot);
    const existing = this.bufferLoads.get(audioBuffer);
    if (existing) return existing;
    const slot = this.slots.findIndex((buffer) => buffer === null);
    if (slot < 0) return Promise.resolve(null);
    const requestId = this.nextRequestId++;
    this.slots[slot] = audioBuffer;
    this.bufferSlots.set(audioBuffer, slot);
    const promise = new Promise((resolve) => {
      this.pending.set(requestId, { resolve, slot, buffer: audioBuffer });
      if (!loadSampleIntoNode(this.node, slot, audioBuffer, requestId)) {
        this.pending.delete(requestId);
        this.bufferSlots.delete(audioBuffer);
        this.slots[slot] = null;
        resolve(null);
      }
    });
    this.bufferLoads.set(audioBuffer, promise);
    return promise;
  }

  async preload(audioBuffers) {
    const unique = [...new Set((audioBuffers || []).filter(Boolean))];
    return Promise.all(unique.map((buffer) => this.load(buffer)));
  }

  schedule(audioBuffer, whenSeconds, sampleRate, options = {}) {
    const slot = this.slotFor(audioBuffer);
    return slot !== null
      ? scheduleSampleOnNode(this.node, slot, whenSeconds, sampleRate, options)
      : false;
  }

  clear(audioBuffer) {
    const slot = audioBuffer ? this.bufferSlots.get(audioBuffer) : undefined;
    if (!Number.isInteger(slot)) return false;
    clearSampleFromNode(this.node, slot);
    this.ready.delete(slot);
    this.bufferSlots.delete(audioBuffer);
    this.bufferLoads.delete(audioBuffer);
    this.slots[slot] = null;
    return true;
  }

  cancelOwner(owner, fadeFrames = 384) {
    cancelSamplePlaybackOwner(this.node, owner, fadeFrames);
  }

  stop() {
    stopSamplePlaybackNode(this.node);
  }

  destroy() {
    this.node?.port?.removeEventListener?.('message', this.handleMessage);
    this.pending.forEach(({ resolve }) => resolve(null));
    this.pending.clear();
    this.ready.clear();
    destroySamplePlaybackNode(this.node);
  }
}

export async function createSamplePlaybackPool(context, destination, capacity = 64) {
  const node = await createSamplePlaybackNode(context);
  if (!node) return null;
  try {
    node.connect(destination);
    return new SamplePlaybackPool(node, capacity);
  } catch (_) {
    destroySamplePlaybackNode(node);
    return null;
  }
}
